/** Minimal SDK call identity used to reconcile notification object updates. */
export interface VoiceCallIdentity {
  readonly id?: string;
  readonly callID?: string;
  readonly callId?: string;
}

export const getVoiceCallId = (call: VoiceCallIdentity): string | null =>
  call.callID ?? call.callId ?? call.id ?? null;

/**
 * SDK notifications may contain a fresh Call object for the same call. Prefer
 * the stable SDK identifier, but retain reference equality when it is absent.
 */
export const callsMatch = (
  first: VoiceCallIdentity,
  second: VoiceCallIdentity,
): boolean => {
  const firstId = getVoiceCallId(first);
  const secondId = getVoiceCallId(second);

  return firstId != null && secondId != null
    ? firstId === secondId
    : first === second;
};

export const isCurrentCallUpdate = (
  currentCall: VoiceCallIdentity | null,
  updatedCall: VoiceCallIdentity,
): boolean => currentCall != null && callsMatch(currentCall, updatedCall);

const OUTBOUND_RINGING_STATES = new Set([
  "new",
  "trying",
  "requesting",
  "recovering",
  "ringing",
  "answering",
  "early",
]);

export const getUpdatedActiveCallState = (
  currentCall: {
    readonly direction: "inbound" | "outbound";
    readonly state: string | null;
  },
  updatedState: string | null | undefined,
): string | null =>
  currentCall.direction === "outbound" &&
  currentCall.state === "ringing" &&
  updatedState != null &&
  OUTBOUND_RINGING_STATES.has(updatedState)
    ? "ringing"
    : (updatedState ?? null);

export const shouldClearIncomingCall = (
  incomingCall: VoiceCallIdentity,
  updatedCall: VoiceCallIdentity,
  updatedState: string,
): boolean =>
  updatedState !== "ringing" && callsMatch(incomingCall, updatedCall);
